import { useRouter } from '@tanstack/react-router';
import { useMemo } from 'react';

import { Icons } from '@/assets/icons/icons';
import { useIsInternalUser } from '@/hooks/useIsInternalUser';

import { createAction, Section } from './actions';

const iconSize = 14;

export const usePlaygroundActions = () => {
  const router = useRouter();
  const isDev = useIsInternalUser();

  return useMemo(
    () =>
      createAction({
        id: 'playground',
        name: 'Playground',
        subtitle: 'Test and experiment with various UI components',
        icon: <Icons.playground size={iconSize} />,
        section: Section.Playground,
        visible: () => isDev,
        children: () =>
          [
            {
              id: 'editor',
              name: 'Editor',
              subtitle: 'Test WSIWIG editor',
              icon: <Icons.write size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/editor' }),
            },
            {
              id: 'markdown-editor',
              name: 'Markdown Editor',
              subtitle: 'Test WSIWIG markdown editor',
              icon: <Icons.code size={iconSize} />,
              section: Section.Playground,
              perform: () =>
                router.navigate({ to: '/playground/markdown-editor' }),
            },
            {
              id: 'notifications',
              name: 'Notifications',
              subtitle: 'Test notification system',
              icon: <Icons.notification size={iconSize} />,
              section: Section.Playground,
              perform: () =>
                router.navigate({ to: '/playground/notifications' }),
            },
            {
              id: 'toasts',
              name: 'Toasts',
              subtitle: 'Test toast notifications',
              icon: <Icons.text size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/toasts' }),
            },
            {
              id: 'banner',
              name: 'Banner',
              subtitle: 'Test banner variants and states',
              icon: <Icons.info size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/banner' }),
            },
            {
              id: 'buttons',
              name: 'Buttons',
              subtitle: 'Test button variants and loading states',
              icon: <Icons.plus size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/buttons' }),
            },
            {
              id: 'typography',
              name: 'Typography',
              subtitle: 'Test typography styles and sizes',
              icon: <Icons.type size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/typography' }),
            },
            {
              id: 'smoothdown',
              name: 'Smoothdown',
              subtitle: 'Preview markdown rendering',
              icon: <Icons.markdown size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/smoothdown' }),
            },
            {
              id: 'editable-tables',
              name: 'Editable Tables',
              subtitle:
                'Explore editable table UX and the expand-to-grid modal',
              icon: <Icons.table size={iconSize} />,
              section: Section.Playground,
              perform: () =>
                router.navigate({ to: '/playground/editable-tables' }),
            },
            {
              id: 'send-draft-card',
              name: 'Send Draft Card',
              subtitle: 'Preview the in-chat email send card states',
              icon: <Icons.send size={iconSize} />,
              section: Section.Playground,
              perform: () =>
                router.navigate({ to: '/playground/send-draft-card' }),
            },
            {
              id: 'action-cards-v2',
              name: 'Action cards v2',
              subtitle: 'Preview the json-render card renderer and playground',
              icon: <Icons.squareCheck size={iconSize} />,
              section: Section.Playground,
              visible: () => isDev,
              perform: () =>
                router.navigate({ to: '/playground/action-cards-v2' }),
            },
            {
              id: 'reasoning',
              name: 'Reasoning',
              subtitle: 'Test reasoning panel states and modalities',
              icon: <Icons.memory size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/reasoning' }),
            },
            {
              id: 'bird-thinking-states',
              name: 'Bird Thinking States',
              subtitle: 'Every state of the thinking bird in a grid',
              icon: <Icons.playground size={iconSize} />,
              section: Section.Playground,
              perform: () =>
                router.navigate({ to: '/playground/bird-thinking-states' }),
            },
            {
              id: 'ask-user-questions',
              name: 'Ask User Questions',
              subtitle: 'Preview the ask_user picker and answered summary',
              icon: <Icons.squareCheck size={iconSize} />,
              section: Section.Playground,
              perform: () =>
                router.navigate({ to: '/playground/ask-user-questions' }),
            },
            {
              id: 'canvas',
              name: 'Canvas components',
              subtitle: 'Browse every Canvas SDK component and state',
              icon: <Icons.file size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/canvas' }),
            },
            {
              id: 'artifacts',
              name: 'Artifacts',
              subtitle: 'Preview completed artifact cards for every file kind',
              icon: <Icons.file size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/artifacts' }),
            },
            {
              id: 'subscription',
              name: 'Subscription',
              subtitle: 'Test subscription components and states',
              icon: <Icons.subscription size={iconSize} />,
              section: Section.Playground,
              perform: () =>
                router.navigate({ to: '/playground/subscription' }),
            },
            {
              id: 'team-plan',
              name: 'Team Plan',
              subtitle: 'Test team plan badge states by tier and role',
              icon: <Icons.attendees size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/team-plan' }),
            },
            {
              id: 'icons',
              name: 'Icons',
              subtitle: 'Browse and copy all available icons',
              icon: <Icons.sparkle size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/icons' }),
            },
            {
              id: 'colors',
              name: 'Colors',
              subtitle: 'Browse and copy all color tokens',
              icon: <Icons.circle size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/colors' }),
            },
            {
              id: 'calendar',
              name: 'Calendar',
              subtitle: 'Test single-date and range date pickers',
              icon: <Icons.calendar size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/calendar' }),
            },
            {
              id: 'fast-scroll-bar',
              name: 'Fast Scroll Bar',
              subtitle: 'Test chat scroll indicator with many messages',
              icon: <Icons.list size={iconSize} />,
              section: Section.Playground,
              perform: () =>
                router.navigate({ to: '/playground/fast-scroll-bar' }),
            },
            {
              id: 'transcription-input-device',
              name: 'Transcription Input Device',
              subtitle: 'Test audio input device selection dropdown',
              icon: <Icons.recordAudio size={iconSize} />,
              section: Section.Playground,
              perform: () =>
                router.navigate({
                  to: '/playground/transcription-input-device',
                }),
            },
            {
              id: 'find',
              name: 'Find in Virtual List',
              subtitle: 'Test Cmd+F search in virtualized lists',
              icon: <Icons.search size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/find' }),
            },
            {
              id: 'editor-find-isolation',
              name: 'Editor / Find Isolation',
              subtitle: 'Verify Cmd+F isolation for editors embedded in lists',
              icon: <Icons.search size={iconSize} />,
              section: Section.Playground,
              perform: () =>
                router.navigate({ to: '/playground/editor-find-isolation' }),
            },
            {
              id: 'whats-new',
              name: "What's New",
              subtitle: 'Test feature announcement modal',
              icon: <Icons.sparkle size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/whats-new' }),
            },
            {
              id: 'live-region',
              name: 'Live Region',
              subtitle: 'Core-pattern reference for active turns in VList',
              icon: <Icons.chat size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/live-region' }),
            },
            {
              id: 'vlist-overlap',
              name: 'VList Overlap',
              subtitle: 'Repro rig for chat rows drawn over each other',
              icon: <Icons.chat size={iconSize} />,
              section: Section.Playground,
              perform: () =>
                router.navigate({ to: '/playground/vlist-overlap' }),
            },
            {
              id: 'meetings-empty-state',
              name: 'Meetings Empty State',
              subtitle: 'Preview the meetings list empty state',
              icon: <Icons.meetings size={iconSize} />,
              section: Section.Playground,
              perform: () =>
                router.navigate({ to: '/playground/meetings-empty-state' }),
            },
            {
              id: 'transcript-streaming',
              name: 'Transcript Streaming',
              subtitle: 'Preview live transcript smoothing and typing dots',
              icon: <Icons.recordAudio size={iconSize} />,
              section: Section.Playground,
              perform: () =>
                router.navigate({ to: '/playground/transcript-streaming' }),
            },
            {
              id: 'empty-states',
              name: 'Empty States',
              subtitle: 'All empty-state variants in one place',
              icon: <Icons.folder size={iconSize} />,
              section: Section.Playground,
              perform: () =>
                router.navigate({ to: '/playground/empty-states' }),
            },
            {
              id: 'widgets',
              name: 'Widgets',
              subtitle: 'Preview home-page widgets and their empty states',
              icon: <Icons.cog size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/widgets' }),
            },
            {
              id: 'sounds',
              name: 'Sounds',
              subtitle: 'Audition every app sound and its triggers',
              icon: <Icons.audio size={iconSize} />,
              section: Section.Playground,
              perform: () => router.navigate({ to: '/playground/sounds' }),
            },
          ].map(createAction),
      }),
    [router, isDev]
  );
};
